import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Catches a render crash so one bad message shape does not white-screen a line
 * cook mid-shift.
 *
 * The recovery button does more than reload, and that part is load-bearing:
 * the service worker serves `/` and `/index.html` cache-first, so a plain
 * `location.reload()` hands back the *same broken bundle* and the user is
 * stuck in a loop with no way out. Clearing the shell cache and unregistering
 * the worker first is what makes "Reload" actually recover.
 */
export default class ErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean; recovering: boolean }
> {
  override state = { failed: false, recovering: false };

  static getDerivedStateFromError() {
    return { failed: true, recovering: false };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Goes to the browser console today. Once the alerting worker is in place
    // this is where a client-side report would be posted from.
    console.error('[ui] render crashed', error, info.componentStack);
  }

  private recover = async () => {
    this.setState({ recovering: true });
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(
          keys.filter((k) => k.startsWith('huddle-shell')).map((k) => caches.delete(k)),
        );
      }
      const registration = await navigator.serviceWorker?.getRegistration();
      await registration?.unregister();
    } catch {
      // Even if cache clearing fails, still reload — a stale bundle is a
      // better outcome than a permanently dead screen.
    }
    location.replace('/');
  };

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="flex h-full flex-col items-center justify-center px-8 text-center">
        <img src="/icon.svg" alt="" className="mb-5 h-14 w-14 rounded-2xl opacity-40" />
        <p className="font-semibold text-zinc-900">Something went wrong</p>
        <p className="mt-1 text-sm text-zinc-500">
          The app hit a problem it couldn't recover from on its own.
        </p>
        <button
          type="button"
          onClick={this.recover}
          disabled={this.state.recovering}
          className="mt-5 rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white active:bg-brand-700 disabled:opacity-50"
        >
          {this.state.recovering ? 'Reloading…' : 'Reload the app'}
        </button>
      </div>
    );
  }
}
